'use client';

import { useState } from 'react';

import { motion } from 'framer-motion';
import { ArrowRight, Loader2, Mail } from 'lucide-react';

import { useTranslation } from '@/lib/i18n/context';

type Props = {
  tool: string;
  onSubmit: () => void;
  onBack: () => void;
};

export function EmailGateStep({ tool, onSubmit, onBack }: Props) {
  const { t, isRTL } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handle = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.includes('@') || submitting) return;
    setSubmitting(true);
    // Lead capture is best-effort and must never block the user. Fire it in the
    // background (keepalive lets it finish even if the page unloads) and proceed
    // immediately, no matter how slow or unresponsive the upstream is.
    void fetch('/api/tools/email-capture', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, tool }),
      keepalive: true,
    }).catch(() => {});
    onSubmit();
  };

  return (
    <motion.div
      key="email"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div>
        <h1 className="mb-2 text-3xl text-black md:text-4xl">
          {t.tools_pages['One last step']}
        </h1>
        <p className="text-neutral-500">
          {
            t.tools_pages[
              'Enter your email to unlock your personalized results.'
            ]
          }
        </p>
      </div>

      <form
        onSubmit={handle}
        className="space-y-4 rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm"
      >
        <div className="relative">
          <Mail
            className={`absolute top-1/2 ${isRTL ? 'right-4' : 'left-4'} size-5 -translate-y-1/2 text-neutral-400`}
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.tools_pages['Enter your work email']}
            className={`w-full rounded-xl border border-neutral-300 py-4 text-base outline-none transition-all focus:border-black focus:ring-2 focus:ring-black/10 ${isRTL ? 'pr-12 pl-4' : 'pr-4 pl-12'}`}
            required
          />
        </div>
        <button
          type="submit"
          disabled={!email.includes('@') || submitting}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-black py-4 font-semibold text-white transition-all hover:bg-neutral-800 disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-400"
        >
          {submitting ? (
            <Loader2 className="size-5 animate-spin" />
          ) : (
            <>
              {t.tools_pages['View my results']}
              <ArrowRight className={`size-5 ${isRTL ? 'rotate-180' : ''}`} />
            </>
          )}
        </button>
        <p className="text-center text-xs text-neutral-400">
          {
            t.tools_pages[
              'Your email may be used by Open to contact you about our product. You can unsubscribe anytime.'
            ]
          }
        </p>
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="mx-auto block text-sm text-neutral-500 underline-offset-4 hover:text-neutral-800 hover:underline disabled:opacity-50"
        >
          {t.tools_pages['Back']}
        </button>
      </form>
    </motion.div>
  );
}
